import { useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Loader2, Lock, ArrowRight, ShoppingCart } from "lucide-react";
import { useLicense } from "../context/LicenseContext";

export const LicenseGate = () => {
  const { unlock } = useLicense();
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [agreed, setAgreed] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!agreed) {
      setError("Please accept the Terms and Safety Disclaimer to continue.");
      return;
    }
    setLoading(true);
    try {
      const r = await unlock(key);
      if (!r.valid) setError("That license key isn't valid. Check it and try again.");
    } catch (err) {
      setError("Couldn't verify right now. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-ink text-white min-h-screen flex items-center justify-center px-4" data-testid="license-gate">
      <div className="w-full max-w-md bg-surface border border-white/10 rounded-2xl p-8 shadow-2xl">
        <Link to="/" className="flex items-center gap-3 mb-8" data-testid="license-gate-brand">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-cyan to-blue-600 text-ink font-heading font-black text-sm">TK</div>
          <span className="font-heading font-extrabold uppercase tracking-[0.16em] text-sm">
            Thortek <span className="text-cyan">Performance</span>
          </span>
        </Link>

        <div className="w-12 h-12 rounded-xl bg-cyan/10 border border-cyan/20 flex items-center justify-center text-cyan">
          <Lock size={22} />
        </div>
        <h1 className="mt-5 font-heading font-black uppercase tracking-tight text-2xl">Unlock the app</h1>
        <p className="mt-2 text-sm text-slate-400 leading-relaxed">
          Enter your lifetime license key to access the full alignment suite. Already bought it? Your key is on your purchase confirmation.
        </p>

        <form onSubmit={submit} className="mt-6">
          <div className="relative">
            <KeyRound size={16} className="absolute start-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="license-key-input"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="THTK-XXXX-XXXX-XXXX-XXXX"
              className="w-full ps-9 pe-4 py-3 rounded-xl bg-ink border border-white/10 font-mono text-sm uppercase tracking-wider text-cyan placeholder:text-slate-600 focus:outline-none focus:border-cyan/50 transition-colors"
            />
          </div>
          {error && <p data-testid="license-error" className="mt-3 text-sm text-fail">{error}</p>}

          <label className="mt-4 flex items-start gap-3 cursor-pointer" data-testid="license-agree-label">
            <input
              type="checkbox"
              data-testid="license-agree-checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-cyan"
            />
            <span className="text-xs text-slate-400 leading-relaxed">
              I agree to the{" "}
              <Link to="/legal/terms" target="_blank" data-testid="agree-terms-link" className="text-cyan hover:underline">Terms of Service</Link>,{" "}
              <Link to="/legal/privacy" target="_blank" data-testid="agree-privacy-link" className="text-cyan hover:underline">Privacy Policy</Link>, and the{" "}
              <Link to="/legal/disclaimer" target="_blank" data-testid="agree-disclaimer-link" className="text-cyan hover:underline">Safety &amp; Liability Disclaimer</Link>.
            </span>
          </label>

          <button
            type="submit"
            data-testid="license-unlock-button"
            disabled={loading || !key.trim() || !agreed}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-cyan text-ink font-heading font-bold uppercase tracking-wide text-sm shadow-xl shadow-cyan/25 hover:bg-cyan-hover disabled:opacity-60 disabled:cursor-not-allowed transition-all"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : null}
            Unlock <ArrowRight size={16} />
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-white/10 text-center">
          <p className="text-sm text-slate-400">Don't have a license yet?</p>
          <Link
            to="/#pricing"
            data-testid="license-buy-link"
            className="mt-3 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-white/15 text-white font-heading font-bold uppercase tracking-wide text-xs hover:border-cyan/50 hover:text-cyan transition-all"
          >
            <ShoppingCart size={15} /> Buy lifetime access — $199.00
          </Link>
        </div>
      </div>
    </div>
  );
};
